import { Injectable, ViewContainerRef, Injector, signal } from '@angular/core';
import { ComponentType } from '@angular/cdk/portal';
import { takeUntil } from 'rxjs';
import { DrawerRef } from './drawer-ref';
import { DrawerOptions, DEFAULT_DRAWER_CONFIG, DrawerSizeConfig, DrawerLayoutConfig, DrawerMode } from './drawer-options';
import { getWidthBreakpointScreen, SCREEN_SIZE_MAX_WIDTH, windowSizeObserver } from '@local/ts-infra';

@Injectable({ providedIn: 'root' })
export class DrawerService {
  private _container: ViewContainerRef = null;

  private readonly BREAKPOINTS_PREVIEW_SIZE = {
    'small-medium': { min: 42, max: 48 },
    medium: { min: 42, max: 58 },
    'medium-large': { min: 32, max: 65 },
    large: { min: 32, max: 70 },
    'extra-large': { min: 32, max: 70 },
  };

  private readonly DEFAULT_DRAWER_SIZE_PERCENTAGE = {
    large: 65,
    'extra-large': 65,
  };

  private readonly ANIMATION_DURATION_MS = 300;

  readonly isOpen = signal<boolean>(false);
  readonly drawerWidth = signal<number>(this.getMinPreviewWidth);
  readonly minPreviewWidth = signal<number>(this.getMinPreviewWidth);
  readonly maxPreviewWidth = signal<number>(this.getMaxPreviewWidth);
  readonly isSmallScreen = signal<boolean>(false);
  readonly disableDragging = signal<boolean>(false);
  readonly drawerUnit = signal<'percent' | 'pixel'>('percent');
  readonly drawerMode = signal<DrawerMode>('side-preview');

  private closeAnimationTimeoutId?: ReturnType<typeof setTimeout>;

  set container(value: ViewContainerRef | null) {
    this._container = value;
  }

  constructor() {
    windowSizeObserver(100).subscribe((value) => {
      this.minPreviewWidth.set(this.getMinPreviewWidth);
      this.maxPreviewWidth.set(this.getMaxPreviewWidth);

      const sizeScreen = SCREEN_SIZE_MAX_WIDTH.medium;
      const isSmallScreen = value.width <= sizeScreen;
      this.isSmallScreen.set(isSmallScreen);
    });
  }

  open<TComponent, TData = any>(
    component: ComponentType<TComponent>,
    data?: TData,
    options?: DrawerOptions,
    injector?: Injector
  ): DrawerRef<TComponent, TData> | null {
    if (!this._container) {
      return;
    }

    if (this.isOpen()) {
      throw new Error('A drawer is already open');
    }

    const drawerRef = new DrawerRef<TComponent, TData>(data, options);

    let componentInjector = injector ?? this._container.injector;

    if (data) {
      componentInjector = Injector.create({
        providers: [
          { provide: DrawerRef, useValue: drawerRef },
          { provide: 'DRAWER_DATA', useValue: data },
          { provide: 'DRAWER_OPTIONS', useValue: options },
        ],
        parent: componentInjector,
      });
    }

    const componentRef = this._container.createComponent(component, {
      injector: componentInjector,
    });

    drawerRef.element = this._container.element;
    drawerRef.compInstance = componentRef.instance as TComponent;

    if (data && componentRef.instance) {
      Object.assign(componentRef.instance, data);
    }

    drawerRef.update$.pipe(takeUntil(drawerRef.destroy$)).subscribe((newData) => {
      Object.assign(componentRef.instance, newData);
      componentRef.changeDetectorRef?.markForCheck();
    });

    drawerRef.close$.pipe(takeUntil(drawerRef.destroy$)).subscribe((value) => {
      this.handleClose(componentRef, value);
    });

    this.configureDrawerLayout(options?.layout);
    this.configureDrawerSize(options?.size);
    this.configureDraggingBehavior(this.drawerMode());

    this.isOpen.set(true);

    return drawerRef;
  }

  private configureDrawerLayout(layoutConfig?: Partial<DrawerLayoutConfig>) {
    const mode = layoutConfig?.mode ?? DEFAULT_DRAWER_CONFIG.layout.mode;
    this.drawerMode.set(mode);
  }

  private configureDrawerSize(sizeConfig?: Partial<DrawerSizeConfig>) {
    const currentMode = this.drawerMode();

    if (currentMode === 'side-drawer') {
      const fixedWidth = sizeConfig?.width;
      this.drawerWidth.set(fixedWidth);
      this.drawerUnit.set('pixel');
    } else if (sizeConfig?.width) {
      this.drawerWidth.set(sizeConfig.width);
      this.drawerUnit.set(sizeConfig.unit ?? 'percent');
    } else {
      this.drawerWidth.set(this.getDefaultDrawerWidth());
      this.drawerUnit.set('percent');
    }
  }

  private configureDraggingBehavior(mode: DrawerMode) {
    if (mode === 'side-preview') {
      this.disableDragging.set(false);
    } else {
      this.disableDragging.set(true);
    }
  }

  private handleClose(componentRef: any, event) {
    this.isOpen.set(false);

    if (this.drawerMode() === 'side-drawer' && event?.via === 'manual_close') {
      this.clearTimeouts();
      this.closeAnimationTimeoutId = setTimeout(() => {
        componentRef.destroy();
        this.closeAnimationTimeoutId = undefined;
      }, this.ANIMATION_DURATION_MS);
    } else {
      componentRef.destroy();
    }
  }

  private clearTimeouts() {
    if (this.closeAnimationTimeoutId) {
      clearTimeout(this.closeAnimationTimeoutId);
      this.closeAnimationTimeoutId = undefined;
    }
  }

  private getBreakpointSize(): string {
    const breakpoint = getWidthBreakpointScreen();

    if (breakpoint === 'medium') {
      const isSmallMedium = window.innerWidth < 1095;
      return isSmallMedium ? 'small-medium' : 'medium';
    }

    if (breakpoint === 'large') {
      const isMediumLarge = window.innerWidth < 1480;
      return isMediumLarge ? 'medium-large' : 'large';
    }

    return breakpoint;
  }

  get getMinPreviewWidth(): number {
    return this.BREAKPOINTS_PREVIEW_SIZE[getWidthBreakpointScreen()]?.min;
  }

  get getMaxPreviewWidth(): number {
    return this.BREAKPOINTS_PREVIEW_SIZE[this.getBreakpointSize()]?.max;
  }

  private getDefaultDrawerWidth(): number {
    const breakpoint = this.getBreakpointSize();
    const defaultPercentage = this.DEFAULT_DRAWER_SIZE_PERCENTAGE[breakpoint];

    if (defaultPercentage) {
      return this.DEFAULT_DRAWER_SIZE_PERCENTAGE[breakpoint];
    } else {
      return this.getMaxPreviewWidth;
    }
  }
}
